/*
  * Copyright Mission 42 Ventures, Inc. All rights reserved.
  * Unauthorized copying, distribution, or use is prohibited.
  */

import React, { useState } from 'react';
import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  Paper,
  Typography,
  makeStyles,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import CloseIcon from '@material-ui/icons/Close';
import { Progress } from '@backstage/core-components';
import { CalledByEntry, Change, DryRunRepo, PortRule, SubmitResult } from './types';
import { useNetworkPolicyApi } from './useNetworkPolicyApi';

const useStyles = makeStyles(theme => ({
  panel: {
    position: 'absolute',
    bottom: theme.spacing(2),
    left: theme.spacing(2),
    zIndex: 10,
    padding: theme.spacing(1.5),
    maxWidth: 420,
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(0.25, 0),
  },
  diff: {
    fontFamily: 'monospace',
    fontSize: 12,
    whiteSpace: 'pre',
    overflowX: 'auto',
    background: theme.palette.background.default,
    padding: theme.spacing(1),
    borderRadius: 4,
  },
  addLine: { background: 'rgba(76,175,80,0.15)', display: 'block' },
  delLine: { background: 'rgba(244,67,54,0.15)', display: 'block' },
  ctxLine: { display: 'block', opacity: 0.65 },
}));

export const changeLabel = (c: Change): string => {
  switch (c.op) {
    case 'addCaller':
      return `${c.service}: allow ${c.caller.name}`;
    case 'removeCaller':
      return `${c.service}: remove ${c.callerName}`;
    case 'setPorts':
      return `${c.service}: ports for ${c.callerName}`;
    case 'enablePolicy':
      return `${c.service}: ENABLE default-deny (${c.callers.length} callers)`;
    case 'promoteEnv':
      return `${c.service}: promote ${c.fromEnv} → ${c.toEnv}`;
    default:
      return 'change';
  }
};

// Order-insensitive port serialization for a key; an omitted protocol is TCP
// (the k8s NetworkPolicyPort default, mirrored from the lib's samePorts), so
// two requests that only differ in an explicit vs. implied TCP don't get
// different keys.
const portsKeyPart = (ports?: PortRule[] | null): string =>
  (ports ?? [])
    .map(p => `${p.protocol ?? 'TCP'}/${p.port}`)
    .sort()
    .join(',');

const envsKeyPart = (envs: string[] | 'all'): string =>
  envs === 'all' ? 'all' : [...envs].sort().join(',');

const callerKeyPart = (c: CalledByEntry): string =>
  `${c.namespace}/${c.name}/${portsKeyPart(c.ports)}`;

// Stable identity for a staged change, independent of its position in the
// array. Index-based keys/removal break once rows carry per-row state or the
// list is reordered/filtered; this key is derived from the change's own
// content instead. Every identity-bearing field of the change is included --
// op, service, caller name+namespace, ports, and envs -- so that, for
// example, staging the same caller twice with different ports (or the same
// caller/ports across different envs) gets distinct keys instead of
// colliding: a collision means React reuses one row/key for two logically
// different staged changes, so un-staging one silently removes both.
export const changeKey = (c: Change): string => {
  switch (c.op) {
    case 'addCaller':
      return [
        'addCaller',
        c.service,
        c.caller.namespace,
        c.caller.name,
        portsKeyPart(c.caller.ports),
        envsKeyPart(c.envs),
      ].join('|');
    case 'removeCaller':
      return [
        'removeCaller',
        c.service,
        c.callerNamespace,
        c.callerName,
        envsKeyPart(c.envs),
      ].join('|');
    case 'setPorts':
      return [
        'setPorts',
        c.service,
        c.callerNamespace,
        c.callerName,
        portsKeyPart(c.ports),
        envsKeyPart(c.envs),
      ].join('|');
    case 'enablePolicy':
      return [
        'enablePolicy',
        c.service,
        [...c.callers].map(callerKeyPart).sort().join(','),
        envsKeyPart(c.envs),
      ].join('|');
    case 'promoteEnv':
      return ['promoteEnv', c.service, c.fromEnv, c.toEnv].join('|');
    default:
      return changeLabel(c);
  }
};

// 'created' is a fresh PR; 'exists' means the backend found an already-open
// PR for this exact change and reused it (not a failure, just a no-op);
// 'updated' means an already-open PR had this change's edits pushed onto it;
// 'failed' means this repo's edit didn't apply (other repos in the same
// submit may still have succeeded, since the backend applies per repo).
const resultSeverity: Record<SubmitResult['status'], 'success' | 'info' | 'error'> = {
  created: 'success',
  exists: 'info',
  updated: 'success',
  failed: 'error',
};

const resultLabel: Record<SubmitResult['status'], string> = {
  created: 'PR opened.',
  exists: 'PR already open.',
  updated: 'Existing PR updated with this change.',
  failed: 'Failed.',
};

type DiffLine = { type: 'ctx' | 'add' | 'del'; text: string };

// Real LCS line diff. A set-based comparison drops added/removed lines whose
// exact text appears elsewhere in the file (common in values YAML, where
// 'ports:' / '- port: 8080' repeat across calledBy entries), which would hide
// changes from the pre-PR review. Values files are small, so O(n*m) is fine.
export const diffLines = (before: string, after: string): DiffLine[] => {
  const a = before.split('\n');
  const b = after.split('\n');
  const m = a.length;
  const n = b.length;
  // lcs[i][j] = LCS length of a[i..] and b[j..]
  const lcs: number[][] = Array.from({ length: m + 1 }, () =>
    new Array(n + 1).fill(0),
  );
  for (let i = m - 1; i >= 0; i--) {
    for (let j = n - 1; j >= 0; j--) {
      lcs[i][j] =
        a[i] === b[j]
          ? lcs[i + 1][j + 1] + 1
          : Math.max(lcs[i + 1][j], lcs[i][j + 1]);
    }
  }
  const out: DiffLine[] = [];
  let i = 0;
  let j = 0;
  while (i < m && j < n) {
    if (a[i] === b[j]) {
      out.push({ type: 'ctx', text: a[i] });
      i++;
      j++;
    } else if (lcs[i + 1][j] >= lcs[i][j + 1]) {
      out.push({ type: 'del', text: a[i] });
      i++;
    } else {
      out.push({ type: 'add', text: b[j] });
      j++;
    }
  }
  while (i < m) out.push({ type: 'del', text: a[i++] });
  while (j < n) out.push({ type: 'add', text: b[j++] });
  return out;
};

const CONTEXT = 2;

const DiffView = ({ before, after }: { before: string; after: string }) => {
  const classes = useStyles();
  const lines = diffLines(before, after);
  // Show changed lines plus CONTEXT lines around them; elide the rest.
  const keep = new Array<boolean>(lines.length).fill(false);
  lines.forEach((l, idx) => {
    if (l.type === 'ctx') return;
    const from = Math.max(0, idx - CONTEXT);
    const to = Math.min(lines.length - 1, idx + CONTEXT);
    for (let k = from; k <= to; k++) keep[k] = true;
  });
  const rendered: React.ReactNode[] = [];
  let eliding = false;
  lines.forEach((l, idx) => {
    if (!keep[idx]) {
      if (!eliding) {
        rendered.push(
          <span key={`e${idx}`} className={classes.ctxLine}>
            {'  …'}
          </span>,
        );
        eliding = true;
      }
      return;
    }
    eliding = false;
    const styles: Record<DiffLine['type'], string> = {
      add: classes.addLine,
      del: classes.delLine,
      ctx: classes.ctxLine,
    };
    const prefixes: Record<DiffLine['type'], string> = {
      add: '+',
      del: '-',
      ctx: ' ',
    };
    rendered.push(
      <span key={idx} className={styles[l.type]}>
        {prefixes[l.type]} {l.text}
      </span>,
    );
  });
  return <div className={classes.diff}>{rendered}</div>;
};

type Props = {
  changes: Change[];
  onRemove: (key: string) => void;
  // keepServices, when provided, names services whose staged changes should
  // be kept rather than cleared (used to retain a failed repo's changes for
  // retry while still clearing the ones that succeeded).
  onClear: (keepServices?: Set<string>) => void;
};

export const StagedChangesPanel = ({ changes, onRemove, onClear }: Props) => {
  const classes = useStyles();
  const api = useNetworkPolicyApi();
  const [reviewOpen, setReviewOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dryRun, setDryRun] = useState<DryRunRepo[] | null>(null);
  const [results, setResults] = useState<SubmitResult[] | null>(null);

  // Keep rendering while the review dialog is open (e.g. showing submit
  // results): unmounting here would tear down the dialog before the user
  // ever sees the PR links.
  if (changes.length === 0 && !reviewOpen) return null;

  const review = async () => {
    setReviewOpen(true);
    setBusy(true);
    setError(null);
    setDryRun(null);
    setResults(null);
    try {
      const res = await api.postChanges(changes, true);
      setDryRun(res.repos ?? []);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await api.postChanges(changes, false);
      // Do NOT clear here: that unmounts the panel (changes -> []) before
      // the PR links ever render. Clearing happens on dialog dismissal.
      setResults(res.results ?? []);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const closeDialog = () => {
    if (busy) return;
    setReviewOpen(false);
    setDryRun(null);
    setError(null);
    if (results) {
      // PRs were opened and the user has seen the links: clear the staged
      // changes for repos that succeeded (created/exists/updated) and
      // refresh the graph, but keep failed repos' changes staged so the
      // user can retry instead of re-entering them from scratch.
      const failedServices = new Set(
        results.filter(r => r.status === 'failed').map(r => r.service),
      );
      setResults(null);
      onClear(failedServices);
    }
  };

  return (
    <>
      {changes.length > 0 && (
        <Paper className={classes.panel} elevation={4}>
          <Typography variant="subtitle2">
            Staged changes ({changes.length})
          </Typography>
          {changes.map(c => (
            <div key={changeKey(c)} className={classes.row}>
              <Typography variant="body2" style={{ flexGrow: 1 }}>
                {changeLabel(c)}
              </Typography>
              {c.op === 'enablePolicy' && (
                <Chip size="small" label="default-deny" color="secondary" />
              )}
              {c.op === 'promoteEnv' && (
                <Chip size="small" label="promote" color="primary" />
              )}
              <IconButton
                size="small"
                aria-label={`unstage ${changeLabel(c)}`}
                onClick={() => onRemove(changeKey(c))}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </div>
          ))}
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={review}
            style={{ marginTop: 8 }}
          >
            Review &amp; submit
          </Button>
        </Paper>
      )}

      <Dialog open={reviewOpen} onClose={closeDialog} maxWidth="md" fullWidth>
        <DialogTitle>Review network-policy changes</DialogTitle>
        <DialogContent>
          {busy && <Progress />}
          {error && <Alert severity="error">{error}</Alert>}
          {results && results.some(r => r.status === 'failed') && (
            <Alert severity="info" style={{ marginBottom: 8 }}>
              Changes for failed repos remain staged so you can retry after
              fixing the issue.
            </Alert>
          )}
          {results &&
            (results.length === 0 ? (
              <Alert severity="info">Nothing to change.</Alert>
            ) : (
              results.map(r => (
                <Alert
                  key={r.repo}
                  severity={resultSeverity[r.status]}
                  style={{ marginBottom: 8 }}
                >
                  {r.repo}: {resultLabel[r.status]}
                  {r.prUrl ? (
                    <>
                      {' '}
                      <Link href={r.prUrl} target="_blank" rel="noopener">
                        {r.prUrl}
                      </Link>
                    </>
                  ) : (
                    <> {r.status === 'failed' ? r.error ?? r.detail : r.detail}</>
                  )}
                </Alert>
              ))
            ))}
          {!results &&
            dryRun &&
            (dryRun.length === 0 ? (
              <Alert severity="info">
                These changes are already reflected in every values file, nothing
                to submit.
              </Alert>
            ) : (
              dryRun.map(repo => (
                <div key={repo.repo} style={{ marginBottom: 16 }}>
                  <Typography variant="subtitle2">
                    {repo.repo} {repo.enablePolicy && '⚠️ enables default-deny'}
                  </Typography>
                  {repo.summaries.map(s => (
                    <Typography key={s} variant="body2">
                      • {s}
                    </Typography>
                  ))}
                  {repo.warnings.map(w => (
                    <Alert key={w} severity="warning" style={{ marginTop: 4, marginBottom: 4 }}>
                      {w}
                    </Alert>
                  ))}
                  {repo.files.map(f => (
                    <div key={f.path}>
                      <Typography variant="caption">{f.path}</Typography>
                      <DiffView before={f.before} after={f.after} />
                    </div>
                  ))}
                </div>
              ))
            ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={busy}>
            {results ? 'Done' : 'Close'}
          </Button>
          {!results && (
            <Button
              variant="contained"
              color="primary"
              disabled={busy || !dryRun || dryRun.length === 0}
              onClick={submit}
            >
              Open PR{dryRun && dryRun.length > 1 ? `s (${dryRun.length})` : ''}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};
